import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Eye, EyeOff } from 'lucide-react';

export default function Login() {
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    const result = isLogin
      ? await login(username, password)
      : await register(username, password, name);

    if (result.success) {
      if (isLogin) {
        navigate('/');
      } else {
        // Show success message for registration
        setSuccessMessage(result.message);
        setUsername('');
        setPassword('');
        setName('');
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1637825891028-564f672aa42c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYWJzdHJhY3QlMjBwdXJwbGUlMjB0ZXh0dXJlfGVufDB8fHx8MTc4MTg0NjgxNnww&ixlib=rb-4.1.0&q=85)',
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md bg-white p-8 shadow-lg">
          <div className="mb-8">
            <h1 className="text-3xl md:text-5xl tracking-tight font-light text-black mb-2">
              Service Reports
            </h1>
            <p className="text-lg text-gray-800 mb-2">PE Conventional Fire Alarm System</p>
            <p className="text-sm md:text-base leading-relaxed text-gray-600">
              {isLogin ? 'Sign in to manage your service reports' : 'Create an account to get started'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="auth-form">
            {!isLogin && (
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-mono text-gray-700">
                  Full Name
                </label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-2 rounded-none bg-gray-200 border-gray-300 focus:ring-violet-600"
                  style={{ color: '#000000' }}
                  required={!isLogin}
                  data-testid="name-input"
                />
              </div>
            )}

            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-mono text-gray-700">
                {isLogin ? 'Username' : 'Username'}
              </label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-2 rounded-none bg-gray-200 border-gray-300 focus:ring-violet-600"
                style={{ color: '#000000' }}
                required
                placeholder={isLogin ? "Enter your username" : "Choose a username"}
                data-testid="username-input"
              />
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-mono text-gray-700">
                Password
              </label>
              <div className="relative mt-2">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-none bg-gray-200 border-gray-300 focus:ring-violet-600 pr-10"
                  style={{ color: '#000000' }}
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-100 border border-red-300 text-red-800 p-3 rounded-none text-sm" data-testid="error-message">
                {error}
              </div>
            )}

            {successMessage && (
              <div className="bg-green-100 border border-green-300 text-green-800 p-3 rounded-none text-sm" data-testid="success-message">
                {successMessage}
              </div>
            )}

            <Button
              type="submit"
              className="w-full rounded-none bg-violet-600 text-white hover:bg-violet-700 font-medium"
              disabled={loading}
              data-testid="submit-button"
            >
              {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
            </Button>
          </form>

          {/* SEO Content - Hidden visually but accessible to search engines */}
          <div className="sr-only" aria-hidden="true">
            <h2>Professional Service Report Management System</h2>
            <p>
              Pecon Service provides a comprehensive digital platform for managing field service operations, 
              maintenance records, and service order documentation. Our cloud-based solution streamlines your 
              workflow with real-time tracking, digital signature capture, and automated report generation.
            </p>
            <h3>Key Features</h3>
            <p>
              Digital signature pads for customer and technician sign-off, automated service number generation 
              (SRC format), parts replacement tracking with RMA numbers, time-in and time-out logging, 
              customizable service types (Ad-hoc, A&A, Testing/Training), comprehensive analytics dashboard 
              with real-time statistics, bulk operations and filtering capabilities, PDF and Excel export 
              functionality, role-based access control (Admin, Manager, Service Person), mobile-responsive 
              design for field technicians, panel DOM tracking and documentation, and complete service history 
              audit trails.
            </p>
            <h3>Benefits for Your Business</h3>
            <p>
              Eliminate paper-based forms and reduce manual data entry. Improve accuracy with digital signatures 
              and automated validation. Access reports anywhere, anytime from any device. Generate professional 
              PDF reports instantly for clients. Track service completion status and technician performance. 
              Maintain complete audit trails for compliance. Secure cloud storage with role-based permissions. 
              Real-time analytics and business insights. Streamline invoicing with parts tracking. Enhance 
              customer satisfaction with faster service delivery.
            </p>
            <h3>Industry Applications</h3>
            <p>
              Field service companies, maintenance teams, equipment repair services, installation contractors, 
              testing and commissioning firms, facility management companies, HVAC service providers, electrical 
              contractors, telecommunications technicians, and any business requiring professional service 
              documentation and reporting.
            </p>
            <h3>Security & Compliance</h3>
            <p>
              Enterprise-grade security with encrypted data transmission, secure authentication with brute-force 
              protection, admin-controlled user approval workflow, audit logs for all report modifications, 
              HTTPS encryption for all communications, and regular backups to prevent data loss. Our system 
              helps you maintain compliance with industry standards and regulatory requirements.
            </p>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
                setSuccessMessage('');
              }}
              className="text-sm text-gray-600 hover:text-black transition-colors"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}